import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	getNotificationsUnreadCounts,
	updateStatusNotification,
} from '../../services/dashboard';

/**
 * Persistent dashboard banner for 90%+ subscription usage alerts.
 *
 * Only the "High Priority Warning" (90%) and "Limit Reached" (100%) tiers
 * ever reach this component — the worker that generates these
 * (check_usage_limits.js) only sets is_important:true at those two
 * thresholds, and the unread-counts endpoint's `important_alerts` field
 * filters on exactly that flag. 50%/75% notifications still reach the user
 * through the existing notification bell in Navbar — this banner isn't meant
 * to duplicate those, only to make the two urgent tiers impossible to miss,
 * per the ticket's "display persistent alerts" requirement for 90%+.
 *
 * Deliberately does its own fetch rather than reading the notification bell's
 * redux state: that state is capped to the 5 most recent notifications of ANY
 * type (see store/reducers/notifications), so a usage alert can fall off it
 * the moment a handful of unrelated notifications land afterward. A banner
 * that can silently disappear because of unrelated activity defeats the
 * "persistent" requirement, so this reads its own untruncated feed instead.
 */
const UsageLimitBanner = () => {
	const navigate = useNavigate();
	const [alerts, setAlerts] = useState([]);

	useEffect(() => {
		let cancelled = false;

		async function fetchImportantAlerts() {
			try {
				const { important_alerts } = await getNotificationsUnreadCounts();
				if (!cancelled) {
					setAlerts(important_alerts || []);
				}
			} catch (error) {
				// Silent — a failed fetch here shouldn't block the rest of the
				// dashboard from rendering, and the notification bell is an
				// independent fallback for the same underlying data.
			}
		}

		fetchImportantAlerts();

		return () => {
			cancelled = true;
		};
	}, []);

	const handleDismiss = async (id) => {
		setAlerts((prev) => prev.filter((alert) => alert.id !== id));
		try {
			await updateStatusNotification({
				status: 'is_read',
				value: true,
				ids: [id],
				mark_as_read: false,
			});
		} catch (error) {
			// Already removed from view — a failed mark-as-read just means it
			// may resurface on next load, not a broken banner right now.
		}
	};

	const handleUpgrade = () => {
		navigate('/subscription-plan');
	};

	if (alerts.length === 0) return null;

	return (
		<div className="d-flex flex-column gap-2 px-3 pt-3">
			{alerts.map((alert) => {
				const isLimitReached = alert.metadata?.threshold >= 100;

				return (
					<div
						key={alert.id}
						className={`alert ${isLimitReached ? 'alert-danger' : 'alert-warning'} d-flex align-items-center justify-content-between mb-0`}
						role="alert"
					>
						<div>
							<strong>{alert.title}</strong>
							<div className="fs-6">{alert.description}</div>
						</div>
						<div className="d-flex align-items-center gap-2 flex-shrink-0">
							<button
								type="button"
								className={`btn btn-sm ${isLimitReached ? 'btn-danger' : 'btn-warning'}`}
								onClick={handleUpgrade}
							>
								Upgrade Plan
							</button>
							<button
								type="button"
								className="btn-close"
								aria-label="Dismiss"
								onClick={() => handleDismiss(alert.id)}
							/>
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default UsageLimitBanner;
